import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import io from 'socket.io-client';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [socket, setSocket] = useState(null);
  const { token, userRole } = useAuth();

  // Debug
  useEffect(() => {
    console.log("NotificationContext - Current token:", token);
    console.log("NotificationContext - Current user role:", userRole);
  }, [token, userRole]);

  const fetchNotifications = useCallback(async () => {
    if (!token) {
      console.log("No token, skipping notification fetch");
      return;
    }

    try {
      console.log("Fetching notifications...");
      const endpoint = 'https://techservicemarket-backend.onrender.com/api/notifications';
      
      const response = await axios.get(endpoint, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'User-Role': userRole || 'user'
        }
      });
      
      console.log('Fetched notifications:', response.data);
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [token, userRole]);

  useEffect(() => {
    if (!token) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    // Set up socket connection
    const newSocket = io('https://techservicemarket-backend.onrender.com', {
      auth: { 
        token,
        userRole: userRole || 'user'
      },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5
    });

    newSocket.on('connect', () => {
      console.log('Socket connected');
      
      try {
        // Extract user ID from token
        const userData = JSON.parse(atob(token.split('.')[1]));
        const userId = userData.id;
        
        if (userId) {
          console.log(`Joining notification room for user ${userId}`);
          newSocket.emit('join', userId);
        }
      } catch (error) {
        console.error('Error extracting user ID from token:', error);
      }
    });

    newSocket.on('notification', (newNotification) => {
      console.log('New notification received:', newNotification);
      
      // Add sound effect for important notifications
      if (newNotification.type === 'interview_scheduled' || 
          newNotification.type === 'application_accepted' || 
          newNotification.type === 'application_rejected') {
        playNotificationSound();
      }
      
      setNotifications(prev => [newNotification, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      // Show browser notification if allowed
      showBrowserNotification(newNotification);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    setSocket(newSocket);
    
    // Fetch initial notifications
    fetchNotifications();

    // Clean up
    return () => {
      if (newSocket) {
        console.log('Closing socket connection');
        newSocket.close();
      }
    };
  }, [token, fetchNotifications, userRole]);

  // Notification sound effect
  const playNotificationSound = () => {
    try {
      const audio = new Audio('/notification-sound.mp3');
      audio.play().catch(e => console.log('Audio play failed:', e));
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  };

  // Browser notification
  const showBrowserNotification = (notification) => {
    if (Notification.permission === 'granted' && document.hidden) {
      // Only show browser notification if the tab is not active
      new Notification('TechServiceMarket', {
        body: notification.message,
        icon: '/logo192.png' // Add your app logo
      });
    } else if (Notification.permission !== 'denied' && Notification.permission !== 'granted') {
      Notification.requestPermission();
    }
  };

  // Set up periodic refresh
  useEffect(() => {
    if (!token) return;
    
    const intervalId = setInterval(() => {
      console.log('Refreshing notifications...');
      fetchNotifications();
    }, 30000); // Every 30 seconds
    
    return () => clearInterval(intervalId);
  }, [token, fetchNotifications]);

  // Handle notification click - navigate to relevant page
  const handleNotificationAction = async (notification) => {
    // Mark as read first
    await markAsRead(notification._id);
    
    // Navigate based on notification type and user role
    if (userRole === 'user') {
      // For regular users, navigate to their applications list
      window.location.href = '/profile/applicationList';
    } else if (userRole === 'recruiter') {
      // For recruiters, navigate to the correct application view
      if (notification.applicationId) {
        // Extract job ID if available in the notification
        const jobId = notification.jobId || null;
        if (jobId) {
          window.location.href = `/recruiter/jobs/${jobId}/applications`;
        } else {
          // If no job ID, go to dashboard
          window.location.href = '/recruiter/dashboard';
        }
      } else {
        window.location.href = '/recruiter/dashboard';
      }
    } else if (userRole === 'universitypartner') {
      // For university partners, similar logic as recruiters
      if (notification.applicationId) {
        const jobId = notification.jobId || null;
        if (jobId) {
          window.location.href = `/university/jobs/${jobId}/applications`;
        } else {
          window.location.href = '/university/dashboard';
        }
      } else {
        window.location.href = '/university/dashboard';
      }
    }
  };

  const markAsRead = async (notificationId) => {
    if (!token || !notificationId) return;
    
    try {
      const endpoint = `https://techservicemarket-backend.onrender.com/api/notifications/${notificationId}/read`;
      
      await axios.put(endpoint, {}, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'User-Role': userRole || 'user'
        }
      });
      
      // Update local state
      setNotifications(prev =>
        prev.map(n =>
          n._id === notificationId ? { ...n, read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!token) return;
    
    try {
      const endpoint = 'https://techservicemarket-backend.onrender.com/api/notifications/mark-all-read';
      
      await axios.put(endpoint, {}, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'User-Role': userRole || 'user'
        }
      });
      
      // Update local state
      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      markAsRead,
      markAllAsRead,
      fetchNotifications,
      handleNotificationAction
    }}>
      {children}
    </NotificationContext.Provider>
  );
}; 